import React, { useState, useEffect } from "react";
import { Checkbox, Tag } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import {
  fetchRehabCenters,
  fetchRehabCentersByLocation,
  fetchLocalRehabCenters,
  fetchLocalRehabCentersByCoords,
} from "../../api/rehabCenter";
import RehabCenterMap from "../RegisterRehabCenter/RehabCenterMap";
import RehabCenterCard from "./RehabCenterCard";
import BookingModal from "../Booking/BookingModal";
import "./RehabCenterLocator.css";

const RehabCenterLocator = ({ loggedInUser, openAuthModal }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [localResults, setLocalResults] = useState([]);
  const [filterOpen247, setFilterOpen247] = useState(false);
  const [isBookingModalVisible, setIsBookingModalVisible] = useState(false);
  const [selectedRehabCenter, setSelectedRehabCenter] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);

  const performSearch = async (currentQuery, is247Filtered) => {
    try {
      const searchLocation = currentQuery || "rehab centers near me";
      const localData = await fetchLocalRehabCenters(
        searchLocation,
        is247Filtered
      );
      setLocalResults(localData);

      const googleData = await fetchRehabCenters(searchLocation, is247Filtered);
      if (googleData?.results) {
        setResults(googleData.results);
        if (googleData.results.length > 0) {
          setMapCenter(googleData.results[0].geometry.location);
        }
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error("Search failed:", error);
      setLocalResults([]);
      setResults([]);
    }
  };

  const performLocationSearch = async (lat, lng, is247Filtered) => {
    try {
      setMapCenter({ lat, lng });

      const localData = await fetchLocalRehabCentersByCoords(
        lat,
        lng,
        is247Filtered
      );
      setLocalResults(localData);

      const googleData = await fetchRehabCentersByLocation(lat, lng);
      if (googleData?.results) {
        setResults(googleData.results);
        // Map is already centered on user's location
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error("Location search failed:", error);
      setLocalResults([]);
      setResults([]);
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          performLocationSearch(latitude, longitude, filterOpen247);
        },
        (error) => {
          console.warn("Geolocation permission denied. Falling back to default search.");
          performSearch("rehab centers near me", filterOpen247);
        }
      );
    } else {
      performSearch("rehab centers near me", filterOpen247);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOpen247]);

  const handleSearch = () => {
    if (query.trim() === "") {
      handleLocationSearch();
    } else {
      performSearch(query, filterOpen247);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleLocationSearch = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          performLocationSearch(latitude, longitude, filterOpen247);
        },
        (error) => {
          alert("Location access denied or unavailable.");
          console.error("Geolocation error:", error);
        }
      );
    } else {
      alert("Geolocation not supported by your browser.");
    }
  };

  const handleBookAppointment = (rehabCenter) => {
    if (loggedInUser) {
      setSelectedRehabCenter(rehabCenter);
      setIsBookingModalVisible(true);
    } else {
      openAuthModal();
    }
  };

  const handleCancelBooking = () => {
    setIsBookingModalVisible(false);
    setSelectedRehabCenter(null);
  };

  const getGoogleOpeningHoursText = (openingHours) => {
    if (!openingHours) return null;
    if (openingHours.permanently_closed) {
      return <Tag color="red">Permanently Closed</Tag>;
    }
    if (
      openingHours.periods &&
      openingHours.periods.length === 1 &&
      openingHours.periods[0].open &&
      !openingHours.periods[0].close
    ) {
      return (
        <Tag color="green">
          <ClockCircleOutlined /> Open 24 hours
        </Tag>
      );
    }
    if (typeof openingHours.open_now !== "undefined") {
      return openingHours.open_now ? (
        <Tag color="green">
          <ClockCircleOutlined /> Open now
        </Tag>
      ) : (
        <Tag color="volcano">
          <ClockCircleOutlined /> Closed
        </Tag>
      );
    }
    if (openingHours.weekday_text) {
      return (
        <Tag>
          <ClockCircleOutlined /> See opening hours
        </Tag>
      );
    }
    return null;
  };

  return (
    <div className="rehab-center-container">
      <div className="rehab-center-search">
        <h2>Find Nearby Rehab Centers</h2>
        <input
          type="text"
          placeholder="Search by Pincode, Address, Locality or Town"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyPress}
          className="search-input"
        />
        <div className="search-buttons">
          <button onClick={handleSearch} className="small-button">
            Search
          </button>
          <button onClick={handleLocationSearch} className="small-button">
            📍 Use My Location
          </button>
        </div>
        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
          <Checkbox
            checked={filterOpen247}
            onChange={(e) => setFilterOpen247(e.target.checked)}
          >
            Show only 24/7 open rehab centers
          </Checkbox>
        </div>

        {localResults.length > 0 && (
          <div className="local-rehab-centers-results">
            <h3
              style={{
                marginTop: "1rem",
                marginBottom: "1rem",
                paddingLeft: "5px",
              }}
            >
              Registered Rehab Centers
            </h3>
            {localResults.map((rehabCenter) => (
              <RehabCenterCard
                key={rehabCenter.id}
                rehabCenter={rehabCenter}
                onBookAppointment={handleBookAppointment}
              />
            ))}
          </div>
        )}

        {results.length > 0 && (
          <>
            <h3 style={{ marginTop: "1rem" }}>
              Rehab Centers from non-registered Users
            </h3>
            <ul>
              {results.map((place, index) => (
                <li key={index} style={{ marginBottom: "0.75rem" }}>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(
                      place.name
                    )}&query_place_id=${place.place_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "underline", color: "#1a73e8" }}
                  >
                    <strong>{place.name}</strong>
                  </a>
                  {getGoogleOpeningHoursText(place.opening_hours) && (
                    <span style={{ marginLeft: "10px" }}>
                      {getGoogleOpeningHoursText(place.opening_hours)}
                    </span>
                  )}
                  <br />
                  {place.formatted_address || place.vicinity}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="rehab-center-map">
        <RehabCenterMap
          rehabCenters={[
            ...results,
            ...localResults
              .filter((p) => p.latitude && p.longitude)
              .map((rehabCenter) => ({
                name: rehabCenter.name,
                address: rehabCenter.address,
                geometry: {
                  location: {
                    lat: rehabCenter.latitude,
                    lng: rehabCenter.longitude,
                  },
                },
              })),
          ]}
          center={mapCenter}
        />
      </div>
      {selectedRehabCenter && (
        <BookingModal
          open={isBookingModalVisible}
          onCancel={handleCancelBooking}
          rehabCenter={selectedRehabCenter}
          user={loggedInUser}
        />
      )}
    </div>
  );
};

export default RehabCenterLocator;